import React, {useEffect, useState} from 'react';
import CloneHistoryPanel from './CloneHistoryPanel';
import './JobDetailsTab.scss';
import {Box, Grid, Typography} from '@mui/material';
import {t} from 'i18next';
import {useCreateNewProtocol} from '../../../../../hooks/ai/review/useReview';
import {
  ICreateNewProtocolPayloadProps,
  IGetAIReviewJobDetailsResponse,
} from '../../../../../api/types/aiReview.type';
import {toCapitalized} from '../../utils';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import ArrButton from '../../../../common/arr-button/ArrButton';
import SaveAsNewProtocolModal, {
  getSaveAsNewProtocolErrorMessage,
} from './SaveAsNewProtocolModal';
import {
  toJobDetailsDisplayText,
  toJobDetailsIssueCodeLines,
} from './jobDetailsDisplay';
import JobDetailsArtifactsTabs from './JobDetailsArtifactsTabs';
import {useJobDetailsArtifacts} from './useJobDetailsArtifacts';
import {
  PROTOCOL_VERSION_DISPLAY_MODE,
  getParentProtocolVersionName,
  getProtocolVersionName,
  resolveProtocolVersionMode,
} from './protocolVersionMode';

export type {ProtocolVersionDisplayMode} from './protocolVersionMode';
export {
  PROTOCOL_VERSION_DISPLAY_MODE,
  getParentProtocolVersionName,
  getProtocolVersionName,
  resolveProtocolVersionMode,
  shouldShowDraftProtocolChangedIcon,
} from './protocolVersionMode';

const ISSUE_CODING = 'ISSUECODE';
const RELEVANCY = 'RELEVANCY';

export const JobDetailsTab: React.FC<{
  jobId: string | number;
  getJobDetailData: IGetAIReviewJobDetailsResponse | null;
  handleRefetchJobDetails: () => void;
}> = ({jobId, getJobDetailData, handleRefetchJobDetails}) => {
  const [selectedJobId, setSelectedJobId] = useState<string | number>(jobId);
  const [isSaveAsNewModalOpen, setIsSaveAsNewModalOpen] = useState(false);
  const [savedAsProtocolName, setSavedAsProtocolName] = useState<string | null>(
    null
  );
  const [saveAsNewError, setSaveAsNewError] = useState<string | null>(null);
  const [showCloneHistory, setShowCloneHistory] = useState(false);

  useEffect(() => {
    setSelectedJobId(jobId);
  }, [jobId]);

  const {mutate: createNewProtocol} = useCreateNewProtocol();
  const jobDetails = getJobDetailData as IGetAIReviewJobDetailsResponse | null;
  const {artifacts, isLoading: isLoadingArtifacts} =
    useJobDetailsArtifacts(jobDetails);

  const displayMode = resolveProtocolVersionMode(jobDetails);
  const protocolVersionName = getProtocolVersionName(jobDetails);
  const parentProtocolVersionName = getParentProtocolVersionName(jobDetails);

  const {createdBy, status, jobType, sourceName, sourceType} = jobDetails || {};

  const getAIReviewJobType = (type: string | undefined) => {
    if (!type) return '';
    switch (type) {
      case ISSUE_CODING:
        return t('ai.review.jobType.issueCoding');
      case RELEVANCY:
        return t('ai.review.jobType.relevancy');
      default:
        return type;
    }
  };

  const handleSaveAsNewProtocol = () => {
    setSaveAsNewError(null);
    setIsSaveAsNewModalOpen(true);
  };

  const handleCloseSaveAsNewModal = () => {
    setSaveAsNewError(null);
    setIsSaveAsNewModalOpen(false);
  };

  const handleSaveAsNewProtocolConfirm = (fileName: string) => {
    if (!jobDetails) {
      return;
    }

    const previousProtocolId =
      jobDetails.parentProtocolID ?? jobDetails.reviewProtocolID;

    if (previousProtocolId == null) {
      return;
    }

    const payload: ICreateNewProtocolPayloadProps = {
      parentProtocolID: jobDetails.parentProtocolID as number | string,
      filename: fileName,
      caseContext: toJobDetailsDisplayText(jobDetails.caseContext),
      relevance: toJobDetailsDisplayText(jobDetails.relevanceCriteria),
      issueCodes: toJobDetailsIssueCodeLines(jobDetails.issueCodes),
    };

    setSaveAsNewError(null);
    createNewProtocol(payload, {
      onSuccess: () => {
        setSavedAsProtocolName(fileName);
        setSaveAsNewError(null);
        setIsSaveAsNewModalOpen(false);
        handleRefetchJobDetails();
      },
      onError: (error) => {
        setSaveAsNewError(getSaveAsNewProtocolErrorMessage(error));
      },
    });
  };

  const handleViewCloneHistoryJobDetails = (cloneHistoryJobId: number) => {
    setSelectedJobId(cloneHistoryJobId);
    setShowCloneHistory(false);
  };

  const renderModifiedProtocolMeta = () => (
    <Box
      className="protocol-version-modified-meta"
      data-testid="job-details-protocol-version-modified-meta"
    >
      <Typography
        component="span"
        className="typo-text label3_size protocol-version-modified-label"
        data-testid="job-details-protocol-version-modified-label"
      >
        {t('ai.review.jobDetailsTab.modifiedProtocol')}
      </Typography>
      <ArrTooltipWrapper
        trigger={
          <InfoIcon
            className="protocol-version-info-icon"
            data-testid="job-details-protocol-version-info-icon"
            aria-hidden="true"
            role="button"
            tabIndex={0}
            aria-label={t('ai.review.jobDetailsTab.modifiedArtifactsTooltip')}
          />
        }
        title={t('ai.review.jobDetailsTab.modifiedArtifactsTooltip')}
      >
        {t('ai.review.jobDetailsTab.modifiedArtifactsTooltip')}
      </ArrTooltipWrapper>
    </Box>
  );

  const renderSaveAsNewButton = () =>
    displayMode === PROTOCOL_VERSION_DISPLAY_MODE.MODIFIED_SAVE_AS_NEW &&
    !savedAsProtocolName ? (
      <ArrButton
        variant="primary"
        size="xs"
        dataTestId="job-details-protocol-save-as-new-button"
        onClick={handleSaveAsNewProtocol}
        className="save-as-new-btn"
      >
        {t('ai.review.jobDetailsTab.saveAsNewProtocol')}
      </ArrButton>
    ) : null;

  const renderProtocolVersionValue = () => {
    if (displayMode === PROTOCOL_VERSION_DISPLAY_MODE.MANUAL) {
      return t('ai.review.jobDetailsTab.manualProtocol');
    }

    if (displayMode === PROTOCOL_VERSION_DISPLAY_MODE.MODIFIED_SAVE_AS_NEW) {
      return (
        <Box
          className="protocol-version-modified"
          data-testid="job-details-protocol-version-modified"
        >
          {parentProtocolVersionName ? (
            <Typography
              component="span"
              className="typo-text label3_size protocol-version-name"
              data-testid="job-details-protocol-version-name"
            >
              {parentProtocolVersionName}
            </Typography>
          ) : null}
          {renderModifiedProtocolMeta()}
          {renderSaveAsNewButton()}
        </Box>
      );
    }

    if (displayMode === PROTOCOL_VERSION_DISPLAY_MODE.MODIFIED_SAVED) {
      return (
        <Box
          className="protocol-version-modified"
          data-testid="job-details-protocol-version-modified"
        >
          {parentProtocolVersionName ? (
            <Typography
              component="span"
              className="typo-text label3_size protocol-version-name"
              data-testid="job-details-protocol-version-name"
            >
              {parentProtocolVersionName}
            </Typography>
          ) : null}
          {protocolVersionName ? (
            <Typography
              component="span"
              className="typo-text label3_size protocol-version-saved-name"
              data-testid="job-details-protocol-version-saved-name"
            >
              {protocolVersionName}
            </Typography>
          ) : null}
          {renderModifiedProtocolMeta()}
        </Box>
      );
    }

    return protocolVersionName;
  };

  const topSectionData: Array<{label: string; value: React.ReactNode}> = [
    {
      label: t('ai.review.jobDetailsTab.jobId'),
      value: jobDetails?.jobId ?? selectedJobId,
    },
    {
      label: t('ai.review.jobDetailsTab.type'),
      value: getAIReviewJobType(jobType),
    },
    {
      label: t('ai.review.jobDetailsTab.createdBy'),
      value: createdBy?.trim(),
    },
    {
      label: t('ai.review.jobDetailsTab.status'),
      value: toCapitalized(status ?? ''),
    },
    {
      label: t('ai.review.jobDetailsTab.selectedSource'),
      value: `${sourceType} - ${sourceName}`,
    },
    {
      label: t('ai.review.jobDetailsTab.protocolVersionUsed'),
      value: renderProtocolVersionValue(),
    },
    ...(displayMode === PROTOCOL_VERSION_DISPLAY_MODE.MODIFIED_SAVED &&
    protocolVersionName
      ? [
          {
            label: t('ai.review.jobDetailsTab.savedAs'),
            value: protocolVersionName,
          },
        ]
      : []),
  ];

  return (
    <Box className="job-details-tab-container">
      <SaveAsNewProtocolModal
        open={isSaveAsNewModalOpen}
        onClose={handleCloseSaveAsNewModal}
        onSave={handleSaveAsNewProtocolConfirm}
        errorMessage={saveAsNewError}
      />
      <Grid
        container
        spacing={2}
        className="top-div"
        data-testid="job-details-top-container"
      >
        {topSectionData.map(({label, value}, index) => (
          <Grid
            size={{xs: 12, sm: 6, md: 4, lg: 'auto'}}
            className="top-section"
            key={label}
            tabIndex={0}
            data-testid="job-details-top-box"
          >
            <Typography
              className="typo-text label3_size top-section-title"
              data-testid={`job-details-top-label-${index}`}
            >
              {label}
            </Typography>
            <Box
              className="typo-text label3_size top-section-text"
              data-testid={`job-details-top-value-${index}`}
            >
              {value}
            </Box>
          </Grid>
        ))}
      </Grid>
      <JobDetailsArtifactsTabs
        jobType={jobType}
        artifacts={artifacts}
        isLoading={isLoadingArtifacts}
      />
      <CloneHistoryPanel
        isOpen={showCloneHistory}
        onClose={() => setShowCloneHistory(false)}
        jobId={jobDetails?.jobId}
        onViewJobDetails={handleViewCloneHistoryJobDetails}
      />
    </Box>
  );
};
